import { Fragment } from 'react'
import { factsOf, ruleSentence, SCOPE_BLURB, SCOPE_WORDS, stagesOf } from '../../lib/improve'
import type { Proposal } from '../../types/improve'

// One candidate rule and everything that was done to it, in the order it was done: typed, checked for
// gaming, replayed deterministically against the corrected item and the whole regression bank, judged only
// where the decider is itself a model, then applied or kept as a negative result. A stage that did not run
// says it did not run; a stage that failed shows what failed. Nothing is summarised into a green tick.

export function Verification({ proposal }: { proposal: Proposal }) {
  const sentence = ruleSentence(proposal.kind, proposal.definition)
  const stages = stagesOf(proposal)
  return (
    <section className={`proposal ${proposal.applied ? 'applied' : 'kept'}`}>
      <header className="proposal-head">
        <div>
          <p className="kicker">{proposal.applied ? 'Applied' : 'Not applied'} · {SCOPE_WORDS[proposal.scope] ?? proposal.scope}</p>
          <h4>{sentence ?? `A ${proposal.kind} rule`}</h4>
          <p className="muted small">{SCOPE_BLURB[proposal.scope]}</p>
        </div>
      </header>

      <dl className="facts compact proposal-facts">
        {factsOf(proposal.definition).map((f) => (
          <Fragment key={f.label}>
            <dt>{f.label}</dt>
            <dd>{f.value}</dd>
          </Fragment>
        ))}
      </dl>
      <p className="proposal-because"><span className="kicker">Because</span> {proposal.because}</p>

      <ol className="verify-timeline">
        {stages.map((s) => (
          <li key={s.key} className={`verify-stage ${s.state}`}>
            <span className="verify-mark" aria-hidden="true" />
            <div className="verify-body">
              <strong>{s.title}</strong>
              <span className={`tag sig ${s.state === 'passed' ? 'ok' : s.state === 'failed' ? 'high' : 'low'}`}>
                {s.state === 'passed' ? 'passed' : s.state === 'failed' ? 'failed' : 'not run'}
              </span>
              <p className="muted small">{s.detail}</p>
              {s.key === 'deterministic' && proposal.deterministic.replays.length > 0 && (
                <table className="grid replay-grid">
                  <thead><tr><th>Replayed</th><th>Should answer</th><th>Answered</th><th /></tr></thead>
                  <tbody>
                    {proposal.deterministic.replays.map((r, i) => (
                      <tr key={`${r.what}-${i}`} className={r.passed ? 'pass' : 'fail'}>
                        <td>{r.what}</td>
                        <td>{r.expected}</td>
                        <td>{r.got}</td>
                        <td className="num">{r.passed ? 'matches' : 'flips'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {s.key === 'judged' && proposal.judged && proposal.judged.samples.length > 0 && (
                <ul className="samples">
                  {proposal.judged.samples.map((v, i) => (
                    <li key={i}><span className="sample-bar" style={{ ['--v' as string]: Math.max(0, Math.min(1, v)) }} /><span className="muted small">{v.toFixed(2)}</span></li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}
